import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PortalShell from '../components/PortalShell';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import { greetingFor, summarisePortal } from '../utils/portalSummary';
import './PortalHome.css';
import './PortalDashboard.css';
import './Uptime.css';
import './CPanel.css';

const CARDS = [
  { path: '/portal/invoices', title: 'Invoices', icon: 'fa-file-invoice-dollar', description: 'View and download your invoices.' },
  { path: '/portal/subscriptions', title: 'Subscriptions', icon: 'fa-rotate', description: 'Your recurring services and next bill.' },
  { path: '/portal/quotes', title: 'Quotes', icon: 'fa-file-signature', description: 'Review, accept or decline quotes.' },
  { path: '/portal/tickets', title: 'Support', icon: 'fa-life-ring', description: 'Open a request or check on one.' },
  { path: '/portal/domains', title: 'Domains', icon: 'fa-globe', description: 'Your domains and when they renew.' },
  { path: '/portal/account', title: 'My Account', icon: 'fa-user-gear', description: 'Details, password and security.' }
];

const STATUS_WORDS = { open: 'Open', in_progress: 'In progress', pending: 'Waiting for you', resolved: 'Resolved', closed: 'Closed' };

const money = (value, currency = 'GBP') => {
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(Number(value) || 0);
  } catch {
    return `£${Number(value || 0).toFixed(2)}`;
  }
};

const shortDate = (value) => (value
  ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '');

// Same state/badge logic as the full Resources page - duplicated rather than
// shared because it's two small pure functions, not worth a module for.
const cpanelStateFor = (account) => {
  if (account.last_error) return 'error';
  if (!account.nproc_limit) return 'unknown';
  const ratio = account.nproc_usage / account.nproc_limit;
  if (ratio >= 1) return 'maxed';
  if (ratio >= 0.8) return 'warn';
  return 'ok';
};

const formatProcessUsage = (account) => {
  const metric = account.usage_snapshot?.nproc;
  if (!metric) return '—';
  if (metric.limit !== null && metric.limit !== undefined) return `${metric.usage}/${metric.limit}`;
  if (metric.usage !== null && metric.usage !== undefined) return `${metric.usage}`;
  return '—';
};

function PortalHome() {
  const [sites, setSites] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [lists, setLists] = useState({});
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Each list fails on its own: a broken one just leaves its panel empty.
        const list = (path) => apiClient.get(path).then((d) => (Array.isArray(d) ? d : [])).catch(() => []);
        const [uptime, cpanel, invoices, subscriptions, quotes, tickets, domains] = await Promise.all([
          apiClient.get('/api/uptime/mine').catch(() => null),
          apiClient.get('/api/cpanel/mine').catch(() => null),
          list('/api/invoices/mine'),
          list('/api/subscriptions/mine'),
          list('/api/quotes/mine'),
          list('/api/tickets/mine'),
          list('/api/domains/mine')
        ]);
        setLists({ invoices, subscriptions, quotes, tickets, domains });
        setSites(uptime?.sites || []);
        setAccounts(cpanel?.accounts || []);
      } finally {
        setStatusLoading(false);
      }
    })();
  }, []);

  const summary = summarisePortal({ ...lists, sites });
  const name = Globals.FirstName || Globals.UserName;
  const attentionCount = summary.attention.length;

  let intro = 'Everything about your account, in one place.';
  if (!statusLoading) {
    intro = attentionCount === 0
      ? 'Everything looks good. Nothing needs your attention right now.'
      : `${attentionCount} thing${attentionCount === 1 ? '' : 's'} could use your attention.`;
  }

  return (
    <PortalShell>
      <div className="ph-root">
        <section className="ph-hero">
          <div>
            <h1>{greetingFor(new Date().getHours())}, {name}</h1>
            <p className="portal-home-intro">{intro}</p>
          </div>
          <div className="ph-hero-actions">
            <Link to="/portal/tickets" className="ph-btn ph-btn-primary"><i className="fas fa-plus" aria-hidden="true"></i> New support request</Link>
            <Link to="/portal/invoices" className="ph-btn"><i className="fas fa-file-invoice-dollar" aria-hidden="true"></i> Invoices</Link>
          </div>
        </section>

        {!statusLoading && attentionCount > 0 && (
          <ul className="ph-attention" aria-label="Needs your attention">
            {summary.attention.map((item) => (
              <li key={item.key}>
                <Link to={item.path} className={`ph-attn ph-attn-${item.tone}`}>
                  <i className={`fas ${item.tone === 'bad' ? 'fa-circle-exclamation' : 'fa-bell'}`} aria-hidden="true"></i>
                  <span>{item.text}</span>
                  <i className="fas fa-chevron-right ph-attn-go" aria-hidden="true"></i>
                </Link>
              </li>
            ))}
          </ul>
        )}

        <div className="ph-stats">
          <Link to="/portal/invoices" className={`ph-stat${summary.overdueCount ? ' is-bad' : ''}`}>
            <span className="ph-stat-icon"><i className="fas fa-sterling-sign" aria-hidden="true"></i></span>
            <span className="ph-stat-label">Outstanding</span>
            <strong className="ph-stat-value">{money(summary.outstanding)}</strong>
            <span className="ph-stat-sub">{summary.overdueCount ? `${summary.overdueCount} overdue` : summary.outstanding ? 'Not yet due' : 'All paid up'}</span>
          </Link>
          <Link to="/portal/tickets" className="ph-stat">
            <span className="ph-stat-icon"><i className="fas fa-life-ring" aria-hidden="true"></i></span>
            <span className="ph-stat-label">Open support requests</span>
            <strong className="ph-stat-value">{summary.openTicketCount}</strong>
            <span className="ph-stat-sub">{summary.openTicketCount ? 'We are on it' : 'Nothing open'}</span>
          </Link>
          <Link to="/portal/subscriptions" className="ph-stat">
            <span className="ph-stat-icon"><i className="fas fa-rotate" aria-hidden="true"></i></span>
            <span className="ph-stat-label">Active services</span>
            <strong className="ph-stat-value">{summary.activeSubscriptionCount}</strong>
            <span className="ph-stat-sub">{summary.monthlyRecurring ? `${money(summary.monthlyRecurring)} / month` : 'Recurring services'}</span>
          </Link>
          <Link to="/portal/domains" className="ph-stat">
            <span className="ph-stat-icon"><i className="fas fa-globe" aria-hidden="true"></i></span>
            <span className="ph-stat-label">Domains</span>
            <strong className="ph-stat-value">{summary.domainCount}</strong>
            <span className="ph-stat-sub">{summary.nextDomain ? `Next renewal: ${summary.nextDomain.info.days < 0 ? 'overdue' : `${summary.nextDomain.info.days} days`}` : 'Registered with us'}</span>
          </Link>
        </div>

        <div className="ph-columns">
          <div className="ph-col">
            <section className="ph-panel">
              <header><h2>Recent support requests</h2><Link to="/portal/tickets">View all</Link></header>
              {summary.recentTickets.length === 0 ? (
                <p className="ph-empty">No requests yet. Need a hand? <Link to="/portal/tickets">Open one</Link>.</p>
              ) : (
                <ul className="ph-list">
                  {summary.recentTickets.map((t) => (
                    <li key={t.ticket_id}>
                      <Link to={`/portal/tickets/${t.ticket_id}`}>
                        <span className="ph-list-main">
                          <span className="ph-list-title">#{t.ticket_number} {t.subject}</span>
                          <span className="ph-list-sub">Opened {shortDate(t.created_at)}</span>
                        </span>
                        <span className={`ph-pill ph-pill-${t.status}`}>{STATUS_WORDS[t.status] || t.status}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="ph-panel">
              <header><h2>Recent invoices</h2><Link to="/portal/invoices">View all</Link></header>
              {summary.recentInvoices.length === 0 ? <p className="ph-empty">No invoices yet.</p> : (
                <ul className="ph-list">
                  {summary.recentInvoices.map((inv) => (
                    <li key={inv.invoice_number}>
                      <Link to="/portal/invoices">
                        <span className="ph-list-main">
                          <span className="ph-list-title">{inv.invoice_number}</span>
                          <span className="ph-list-sub">{shortDate(inv.creation_date)}</span>
                        </span>
                        <span className="ph-amount">{money(inv.amount_payable, inv.currency)}</span>
                        <span className={`ph-pill ${inv.payment_status === 'Paid' ? 'ph-pill-resolved' : 'ph-pill-pending'}`}>{inv.payment_status === 'Paid' ? 'Paid' : 'Due'}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <div className="ph-col">
            <section className="ph-panel">
              <header><h2>Coming up</h2></header>
              {summary.upcoming.length === 0 ? <p className="ph-empty">Nothing renewing in the next 60 days.</p> : (
                <ul className="ph-list">
                  {summary.upcoming.map((e) => (
                    <li key={`${e.kind}-${e.label}`}>
                      <Link to={e.kind === 'domain' ? '/portal/domains' : '/portal/subscriptions'}>
                        <span className={`ph-badge-icon ph-badge-${e.info.tone}`}><i className={`fas ${e.kind === 'domain' ? 'fa-globe' : 'fa-rotate'}`} aria-hidden="true"></i></span>
                        <span className="ph-list-main">
                          <span className="ph-list-title">{e.label}</span>
                          <span className="ph-list-sub">{e.kind === 'domain' ? 'Domain renewal' : 'Next invoice'} &middot; {shortDate(e.date)}</span>
                        </span>
                        {e.amount ? <span className="ph-amount">{money(e.amount, e.currency)}</span> : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {!statusLoading && sites.length > 0 && (
              <Link to="/portal/uptime" className="ph-panel ph-panel-link">
                <header><h2>Site status</h2><span>Details</span></header>
                <ul className="portal-status-list">
                  {sites.map((site) => (
                    <li key={site.hostname}>
                      <span className={`uptime-dot uptime-dot-${site.state}`} aria-hidden="true"></span>
                      <span className="portal-status-name">{site.hostname}</span>
                      <span className={`uptime-badge uptime-badge-${site.state}`}>{site.state}</span>
                    </li>
                  ))}
                </ul>
              </Link>
            )}

            {!statusLoading && accounts.length > 0 && (
              <Link to="/portal/cpanel" className="ph-panel ph-panel-link">
                <header><h2>Resource usage</h2><span>Details</span></header>
                <ul className="portal-status-list">
                  {accounts.map((account) => {
                    const state = cpanelStateFor(account);
                    return (
                      <li key={account.cpanel_username}>
                        <span className={`cpanel-dot cpanel-dot-${state}`} aria-hidden="true"></span>
                        <span className="portal-status-name">{account.domain || account.cpanel_username}</span>
                        <span className={`cpanel-nproc cpanel-nproc-${state}`}>{formatProcessUsage(account)}</span>
                      </li>
                    );
                  })}
                </ul>
              </Link>
            )}
          </div>
        </div>

        <h2 className="ph-section-title">Jump to</h2>
        <div className="ph-links">
          {CARDS.map((card) => (
            <Link key={card.path} to={card.path} className="ph-link">
              <span className="ph-link-icon"><i className={`fas ${card.icon}`} aria-hidden="true"></i></span>
              <span><strong>{card.title}</strong><small>{card.description}</small></span>
            </Link>
          ))}
        </div>
      </div>
    </PortalShell>
  );
}

export default PortalHome;
